/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  CHART_HEIGHT,
  ChartCard,
  ChartEmpty,
  PRODUTIVIDADE_ROW_HEIGHT,
} from './chartShared';
import {
  aggregatePendentesPorGerencia,
  getItemPendentes,
  getUnidadeLabel,
} from './commandCenterConstants';

const plotFrameSx = {
  height: CHART_HEIGHT,
  overflowX: 'hidden',
  overflowY: 'hidden',
};

const RankingCharts = ({
  cargaItems,
  lenteId,
  loading,
  onSelectDigitador,
  onSelectGerencia,
}) => {
  const theme = useTheme();
  const unidade = getUnidadeLabel(lenteId);

  const pendentesRows = useMemo(() => (
    (cargaItems || [])
      .map((item) => ({
        label: item.digitadorNome || '—',
        digitadorId: item.digitadorId,
        digitadorNome: item.digitadorNome,
        pendentes: getItemPendentes(item),
      }))
      .filter((row) => row.pendentes > 0)
      .sort((a, b) => b.pendentes - a.pendentes)
  ), [cargaItems]);

  const gerenciaRows = useMemo(
    () => aggregatePendentesPorGerencia(cargaItems),
    [cargaItems],
  );

  const pendentesInnerHeight = Math.max(
    120,
    pendentesRows.length * PRODUTIVIDADE_ROW_HEIGHT + 48,
  );

  const handlePendentesClick = useCallback((_event, identifier) => {
    if (!onSelectDigitador || identifier?.dataIndex == null) return;
    const row = pendentesRows[identifier.dataIndex];
    if (row?.digitadorId) {
      onSelectDigitador({ digitadorId: row.digitadorId, nome: row.digitadorNome });
    }
  }, [onSelectDigitador, pendentesRows]);

  const handleGerenciaClick = useCallback((_event, identifier) => {
    if (!onSelectGerencia || identifier?.dataIndex == null) return;
    const row = gerenciaRows[identifier.dataIndex];
    if (row?.gerenciaId) onSelectGerencia(row);
  }, [onSelectGerencia, gerenciaRows]);

  const chartSx = {
    width: '100%',
    maxWidth: '100%',
    ml: 0,
    cursor: 'pointer',
  };

  return (
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
        Pendentes
      </Typography>
      <Grid container spacing={2} alignItems="stretch">
        <Grid size={{ xs: 12, lg: 6 }}>
          <ChartCard title={`Por digitador (${unidade})`}>
            {loading ? (
              <Skeleton variant="rounded" height={CHART_HEIGHT} />
            ) : !pendentesRows.length ? (
              <ChartEmpty message="Nenhum pendente nesta lente" />
            ) : (
              <Box
                sx={{
                  ...plotFrameSx,
                  overflowY: pendentesInnerHeight > CHART_HEIGHT ? 'auto' : 'hidden',
                }}
              >
                <BarChart
                  layout="horizontal"
                  dataset={pendentesRows}
                  series={[{
                    dataKey: 'pendentes',
                    label: 'Pendentes',
                    color: theme.palette.warning.main,
                  }]}
                  yAxis={[{
                    scaleType: 'band',
                    dataKey: 'label',
                    width: 'auto',
                    disableTicks: true,
                    tickLabelStyle: { fontSize: 11 },
                  }]}
                  xAxis={[{ min: 0 }]}
                  height={Math.max(CHART_HEIGHT, pendentesInnerHeight)}
                  margin={{ left: 8, right: 16, top: 8, bottom: 24 }}
                  onItemClick={handlePendentesClick}
                  hideLegend
                  sx={chartSx}
                />
              </Box>
            )}
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <ChartCard title="Por gerência">
            {loading ? (
              <Skeleton variant="rounded" height={CHART_HEIGHT} />
            ) : !gerenciaRows.length ? (
              <ChartEmpty message="Sem carga por gerência nesta lente" />
            ) : (
              <Box sx={plotFrameSx}>
                <BarChart
                  dataset={gerenciaRows}
                  series={[{
                    dataKey: 'pendentes',
                    label: 'Pendentes',
                    color: theme.palette.warning.main,
                  }]}
                  xAxis={[{
                    scaleType: 'band',
                    dataKey: 'sigla',
                  }]}
                  yAxis={[{ min: 0 }]}
                  height={CHART_HEIGHT}
                  onItemClick={handleGerenciaClick}
                  hideLegend
                  sx={chartSx}
                />
              </Box>
            )}
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

RankingCharts.propTypes = {
  cargaItems: PropTypes.arrayOf(PropTypes.object),
  lenteId: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  onSelectDigitador: PropTypes.func,
  onSelectGerencia: PropTypes.func,
};

RankingCharts.defaultProps = {
  cargaItems: [],
  loading: false,
  onSelectDigitador: null,
  onSelectGerencia: null,
};

export default RankingCharts;
